// "New item" dialog: create (or edit) generated media — color matte, gradient,
// bars & tone, titles, countdown leader — with a live preview at the sequence's
// aspect ratio. Created items land in the Media bin; "Add & place" also drops
// them at the playhead.
import { Store } from "../state/store";
import { TextSpec } from "../state/model";
import { el } from "../util";
import { openModal, toast } from "./modal";
import { VisualSpec, VisualType, GENERATOR_LABELS, FONTS, TEXT_PRESETS, defaultSpec, drawGenerator, generatorDurations } from "../video/generators";

const TYPES: { type: VisualType; icon: string }[] = [
  { type: "color", icon: "■" }, { type: "gradient", icon: "◩" }, { type: "bars", icon: "▥" },
  { type: "text", icon: "T" }, { type: "countdown", icon: "⏲" },
];
const SWATCHES = ["#000000", "#ffffff", "#808080", "#1e3a8a", "#16a34a", "#dc2626", "#f59e0b", "#7c3aed", "#00b140"];

export function openGenerateDialog(
  store: Store,
  opts: { editAssetId?: string; type?: VisualType; textPreset?: keyof typeof TEXT_PRESETS; place?: (assetId: string) => void } = {},
) {
  const editing = opts.editAssetId ? store.asset(opts.editAssetId) : undefined;
  let spec: VisualSpec = editing?.generator ? JSON.parse(JSON.stringify(editing.generator)) : defaultSpec(opts.type ?? "color");
  if (!editing && spec.type === "text" && opts.textPreset) spec = { ...spec, ...TEXT_PRESETS[opts.textPreset] } as TextSpec;
  let nameTouched = !!editing;
  const p = store.project;

  const root = el("div", { class: "gen-dialog" });
  const typeBar = el("div", { class: "gen-types" });
  const preview = document.createElement("canvas");
  preview.className = "gen-preview";
  const PW = 384, PH = Math.round(PW * (p.height / p.width));
  preview.width = PW * 2; preview.height = PH * 2; // crisp on hi-dpi
  preview.style.width = PW + "px"; preview.style.height = PH + "px";
  const scrub = el("input", { attrs: { type: "range", min: "0", max: "1", step: "0.01", value: "0", title: "Preview time (countdown)" } }) as HTMLInputElement;
  const optsEl = el("div", { class: "gen-opts fx-editor" });
  const nameIn = el("input", { attrs: { placeholder: "Name" } }) as HTMLInputElement;
  nameIn.addEventListener("input", () => { nameTouched = true; });
  const durIn = el("input", { attrs: { type: "number", min: "0.1", step: "0.5", title: "Length of a new clip when placed (you can trim it longer)" } }) as HTMLInputElement;
  durIn.style.width = "70px";
  durIn.value = String(editing?.defaultDuration ?? generatorDurations(spec).defaultDuration);
  const durLabel = el("label", { class: "hint", text: "Clip length (s)" });
  const countLen = el("span", { class: "hint gen-countlen" });
  /** A countdown's length is its starting number (one second per number), so
   *  it gets a readout instead of the free-form clip-length field. */
  const syncLengthUi = () => {
    const cd = spec.type === "countdown";
    durLabel.style.display = durIn.style.display = cd ? "none" : "";
    countLen.style.display = cd ? "" : "none";
    if (spec.type === "countdown") countLen.textContent = `Length ${spec.from} s — one second per number`;
  };

  const redraw = () => {
    const g = preview.getContext("2d")!;
    g.clearRect(0, 0, preview.width, preview.height);
    // checkerboard shows transparency (titles overlay the tracks below them)
    const c = 16;
    for (let y = 0; y < preview.height; y += c) for (let x = 0; x < preview.width; x += c) {
      g.fillStyle = ((x + y) / c) % 2 ? "#2a2f3a" : "#1f232c"; g.fillRect(x, y, c, c);
    }
    const t = spec.type === "countdown" ? Number(scrub.value) * spec.from : 0;
    drawGenerator(g, spec, preview.width, preview.height, t);
    scrub.style.display = spec.type === "countdown" ? "" : "none";
  };
  scrub.addEventListener("input", redraw);

  const setType = (type: VisualType) => {
    if (type !== spec.type) spec = defaultSpec(type);
    if (!nameTouched) nameIn.value = spec.type === "text" ? (spec.text.split("\n")[0] || "Title").slice(0, 40) : GENERATOR_LABELS[type];
    if (!editing) durIn.value = String(generatorDurations(spec).defaultDuration);
    for (const b of typeBar.querySelectorAll("button")) b.classList.toggle("on", (b as any)._type === type);
    buildOptions();
    syncLengthUi();
    redraw();
  };
  for (const t of TYPES) {
    const b = el("button", { class: "gen-type", text: `${t.icon}  ${GENERATOR_LABELS[t.type]}` }) as HTMLButtonElement;
    (b as any)._type = t.type;
    if (editing && t.type !== spec.type) b.disabled = true; // an existing item keeps its kind
    b.addEventListener("click", () => setType(t.type));
    typeBar.append(b);
  }

  // ---- option builders ----
  const row = (label: string, ...ctl: HTMLElement[]) => el("div", { class: "fx-param gen-row" }, [el("label", { text: label }), ...ctl]);
  const color = (val: string, on: (v: string) => void) => {
    const i = el("input", { attrs: { type: "color", value: val } }) as HTMLInputElement;
    i.value = val;
    i.addEventListener("input", () => { on(i.value); redraw(); });
    return i;
  };
  const range = (min: number, max: number, step: number, val: number, fmt: (v: number) => string, on: (v: number) => void) => {
    const s = el("input", { attrs: { type: "range", min: String(min), max: String(max), step: String(step), value: String(val) } }) as HTMLInputElement;
    s.value = String(val);
    const out = el("output", { text: fmt(val) });
    s.addEventListener("input", () => { const v = Number(s.value); out.textContent = fmt(v); on(v); redraw(); });
    return [s, out] as const;
  };
  const check = (label: string, val: boolean, on: (v: boolean) => void) => {
    const c = el("input", { attrs: { type: "checkbox" } }) as HTMLInputElement;
    c.checked = val;
    c.addEventListener("change", () => { on(c.checked); redraw(); buildOptions(); });
    return el("label", { class: "gen-check" }, [c, el("span", { text: label })]);
  };
  const pct = (v: number) => `${Math.round(v * 100)}%`;

  function buildOptions() {
    optsEl.innerHTML = "";
    const s = spec;
    if (s.type === "color") {
      const sw = el("div", { class: "gen-swatches" });
      const picker = color(s.color, (v) => { s.color = v; });
      for (const c of SWATCHES) {
        const b = el("button", { class: "gen-swatch", attrs: { title: c } }); b.style.background = c;
        b.addEventListener("click", () => { s.color = c; picker.value = c; redraw(); });
        sw.append(b);
      }
      optsEl.append(row("Color", picker, el("span")), sw, el("p", { class: "hint", text: "A black matte is the classic slug / filler; green (#00b140) works for keying tests." }));
    } else if (s.type === "gradient") {
      optsEl.append(row("From", color(s.from, (v) => { s.from = v; }), el("span")), row("To", color(s.to, (v) => { s.to = v; }), el("span")));
      optsEl.append(row("Angle", ...range(0, 360, 1, s.angle, (v) => `${v}°`, (v) => { s.angle = v; })));
    } else if (s.type === "bars") {
      optsEl.append(check("Include reference tone (linked audio clip)", s.tone, (v) => { s.tone = v; }));
      if (s.tone) {
        optsEl.append(row("Tone", ...range(100, 10000, 10, s.freq, (v) => `${v} Hz`, (v) => { s.freq = v; })));
        optsEl.append(row("Level", ...range(-40, 0, 1, s.db, (v) => `${v} dBFS`, (v) => { s.db = v; })));
      }
      optsEl.append(el("p", { class: "hint", text: "SMPTE-style 75% bars with PLUGE; the standard reference tone is 1 kHz at −20 dBFS. Up to 60 s long." }));
    } else if (s.type === "countdown") {
      optsEl.append(row("Count from", ...range(3, 10, 1, s.from, (v) => `${v} s`, (v) => { s.from = v; syncLengthUi(); })));
      optsEl.append(row("Background", color(s.bg, (v) => { s.bg = v; }), el("span")), row("Accent", color(s.accent, (v) => { s.accent = v; }), el("span")));
      optsEl.append(check("Beep on each second (linked audio clip)", s.beep, (v) => { s.beep = v; }));
    } else if (s.type === "text") {
      const presets = el("div", { class: "clip-actions" });
      for (const [k, label] of [["title", "Title"], ["lower", "Lower third"], ["subtitle", "Subtitle"]] as const) {
        const b = el("button", { text: label });
        b.addEventListener("click", () => { const keep = s.text; Object.assign(s, TEXT_PRESETS[k]); if (keep && keep !== "Title") s.text = keep; buildOptions(); redraw(); });
        presets.append(b);
      }
      const ta = el("textarea", { class: "gen-text", attrs: { rows: "3", placeholder: "Type your text (Enter = new line)" } }) as HTMLTextAreaElement;
      ta.value = s.text;
      ta.addEventListener("input", () => { s.text = ta.value; if (!nameTouched) nameIn.value = (ta.value.split("\n")[0] || "Title").slice(0, 40); redraw(); });
      const font = el("select") as HTMLSelectElement;
      for (const f of FONTS) { const o = el("option", { text: f, attrs: { value: f } }) as HTMLOptionElement; o.style.fontFamily = f; font.append(o); }
      font.value = s.font;
      font.addEventListener("change", () => { s.font = font.value; redraw(); });
      const tog = (label: string, on: boolean, fn: () => void, title: string) => {
        const b = el("button", { class: "mini toggle" + (on ? " on" : ""), text: label, attrs: { title } });
        b.addEventListener("click", () => { fn(); b.classList.toggle("on"); redraw(); });
        return b;
      };
      const style = el("div", { class: "clip-actions" }, [
        tog("B", s.bold, () => { s.bold = !s.bold; }, "Bold"),
        tog("I", s.italic, () => { s.italic = !s.italic; }, "Italic"),
        tog("⯇", s.align === "left", () => { s.align = "left"; buildOptions(); }, "Align left"),
        tog("≡", s.align === "center", () => { s.align = "center"; buildOptions(); }, "Center"),
        tog("⯈", s.align === "right", () => { s.align = "right"; buildOptions(); }, "Align right"),
        tog("Shadow", s.shadow, () => { s.shadow = !s.shadow; }, "Drop shadow"),
      ]);
      const bgSel = el("select") as HTMLSelectElement;
      for (const [v, l] of [["none", "None (transparent)"], ["box", "Box behind text"], ["full", "Full frame"]] as const) bgSel.append(el("option", { text: l, attrs: { value: v } }));
      bgSel.value = s.bg;
      bgSel.addEventListener("change", () => { s.bg = bgSel.value as TextSpec["bg"]; buildOptions(); redraw(); });
      optsEl.append(presets, ta, row("Font", font, el("span")), style,
        row("Size", ...range(0.02, 0.3, 0.005, s.size, pct, (v) => { s.size = v; })),
        row("Color", color(s.color, (v) => { s.color = v; }), el("span")),
        row("X", ...range(0, 1, 0.005, s.x, pct, (v) => { s.x = v; })),
        row("Y", ...range(0, 1, 0.005, s.y, pct, (v) => { s.y = v; })),
        row("Outline", ...range(0, 0.25, 0.005, s.outline, (v) => (v ? pct(v) : "off"), (v) => { s.outline = v; })),
      );
      if (s.outline > 0) optsEl.append(row("Outline color", color(s.outlineColor, (v) => { s.outlineColor = v; }), el("span")));
      optsEl.append(row("Background", bgSel, el("span")));
      if (s.bg !== "none") {
        optsEl.append(row("Bg color", color(s.bgColor, (v) => { s.bgColor = v; }), el("span")));
        optsEl.append(row("Bg opacity", ...range(0, 1, 0.01, s.bgOpacity, pct, (v) => { s.bgOpacity = v; })));
      }
      optsEl.append(el("p", { class: "hint", text: "Titles have a transparent background, so put them on a video track above your footage (Add & place does this for you)." }));
    }
  }

  root.append(
    typeBar,
    el("div", { class: "gen-main" }, [
      el("div", { class: "gen-left" }, [preview, scrub, el("div", { class: "gen-meta" }, [nameIn, durLabel, durIn, countLen])]),
      optsEl,
    ]),
  );
  setType(spec.type);
  if (editing) nameIn.value = editing.name;

  const defaultDur = () => {
    if (spec.type === "countdown") return spec.from; // always the full count
    const v = Number(durIn.value); return v > 0 ? v : undefined;
  };
  const create = () => store.addGeneratedAsset(JSON.parse(JSON.stringify(spec)), nameIn.value.trim() || GENERATOR_LABELS[spec.type], defaultDur());
  const actions = editing
    ? [
        { label: "Cancel", onClick: () => {} },
        { label: "Save changes", primary: true, onClick: () => { store.updateGenerator(editing.id, JSON.parse(JSON.stringify(spec)), nameIn.value.trim() || editing.name, defaultDur()); toast("Updated — every clip using this item changed."); } },
      ]
    : [
        { label: "Cancel", onClick: () => {} },
        { label: "Add to Media", onClick: () => { const a = create(); toast(`Added “${a.name}” to Media.`); } },
        ...(opts.place ? [{ label: "Add & place at playhead", primary: true, onClick: () => { const a = create(); opts.place!(a.id); } }] : []),
      ];
  openModal(editing ? `Edit ${GENERATOR_LABELS[spec.type].toLowerCase()}` : "New item", root, actions);
}
